import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Skeleton,
  Tooltip,
  Link,
} from '@mui/material';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import api from '../../services/api';

// Compact "live counters" strip above the approvals queue on the admin Home
// page. Each card shows just an icon, short label, and the current count;
// every bit of explanatory text lives inside the tooltip so the strip stays
// readable on narrow viewports. Cards link to a filtered view where useful.

const POLL_MS = 30000;

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return null;
  }
};

const ReservationCard = ({
  icon,
  label,
  value,
  tooltip,
  accent,
  onClick,
  loading,
}) => {
  const card = (
    <Paper
      variant="outlined"
      sx={{
        px: 1.25,
        py: 0.75,
        flex: '1 1 0',
        minWidth: { xs: 'calc(50% - 6px)', sm: 120 },
        cursor: onClick ? 'pointer' : 'help',
        transition: 'background-color 0.15s ease',
        '&:hover': onClick ? { backgroundColor: 'action.hover' } : undefined,
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : -1}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {icon}
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton width={40} height={28} />
      ) : (
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            color: accent || 'text.primary',
          }}
        >
          {value}
        </Typography>
      )}
    </Paper>
  );

  if (!tooltip) return card;
  return (
    <Tooltip title={tooltip} placement="top" arrow enterTouchDelay={0}>
      {card}
    </Tooltip>
  );
};

const ReservationsStrip = ({ refreshNonce }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/registration-flow/summary');
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load registration flow summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const states = summary?.states || {};
  const thresholds = summary?.thresholds || {};

  const soonestExpiry = formatDate(states.approved_unpaid?.soonest_expiry);
  const softHeldBooths = states.soft_held?.booths || 0;
  const pendingStale = states.pending_approval?.stale_vendors || 0;

  const softHeldTip = [
    `Vendors in onboarding with a booth held.`,
    softHeldBooths
      ? `${softHeldBooths} booth${softHeldBooths === 1 ? '' : 's'} held right now.`
      : null,
    `Auto-releases after ${thresholds.soft_hold_ttl_minutes ?? 60} idle minutes.`,
  ]
    .filter(Boolean)
    .join(' ');

  const pendingTip = [
    'Vendors waiting on admin review.',
    pendingStale > 0
      ? `${pendingStale} have been waiting more than ${thresholds.pending_nudge_days ?? 3} days.`
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  const awaitingPaymentTip = [
    'Approved vendors who still owe payment.',
    soonestExpiry
      ? `Soonest hold expires ${soonestExpiry}.`
      : `${thresholds.approved_payment_window_days ?? 14}-day payment window.`,
  ]
    .filter(Boolean)
    .join(' ');

  const profileAbandonTip = `Vendors idle more than ${
    thresholds.profile_abandon_hours ?? 24
  }h who never picked a booth.`;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <ReservationCard
          icon={<HourglassBottomIcon fontSize="small" color="info" />}
          label="Soft held"
          value={states.soft_held?.vendors ?? 0}
          tooltip={softHeldTip}
          onClick={() => navigate('/admin/registration-flow')}
          loading={loading && !summary}
        />
        <ReservationCard
          icon={<PendingActionsIcon fontSize="small" color="warning" />}
          label="Pending"
          value={states.pending_approval?.vendors ?? 0}
          accent={pendingStale > 0 ? 'warning.main' : undefined}
          tooltip={pendingTip}
          onClick={() => navigate('/admin/approvals?status=pending')}
          loading={loading && !summary}
        />
        <ReservationCard
          icon={<PaymentIcon fontSize="small" color="success" />}
          label="Awaiting pay"
          value={states.approved_unpaid?.vendors ?? 0}
          accent={states.approved_unpaid?.vendors > 0 ? 'success.main' : undefined}
          tooltip={awaitingPaymentTip}
          onClick={() => navigate('/admin/payments')}
          loading={loading && !summary}
        />
        <ReservationCard
          icon={<PersonOffIcon fontSize="small" color="error" />}
          label="Abandoned"
          value={states.profile_abandoners?.vendors ?? 0}
          accent={states.profile_abandoners?.vendors > 0 ? 'error.main' : undefined}
          tooltip={profileAbandonTip}
          onClick={() => navigate('/admin/approvals?status=incomplete')}
          loading={loading && !summary}
        />
      </Stack>
      <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/admin/registration-flow')}
        >
          See registration flow →
        </Link>
      </Box>
    </Box>
  );
};

export default ReservationsStrip;
